import { useOptionalQuestionnaireParams } from 'hooks/useQuestionnaireParams'
import useRenderedQuestionnaire from 'hooks/useRenderedQuestionnaire'
import useRespondentProgress, {
  clearPersistedRespondentProgress
} from 'hooks/useRespondentProgress'
import useSurveyLocales, { SurveyLocales } from 'hooks/useSurveyLocales'
import { RenderedQuestionnaire } from 'model/questionnaire'
import { RespondentProgress } from 'model/respondentProgress'
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useMount, useUpdateEffect } from 'react-use'
import { noContextMethod } from 'utils/context'
import { clearQuestionsHiddenByDisplayLogic } from 'utils/hiddenByDisplayLogic'
import { clearQuestionsHiddenByLooping } from 'utils/hiddenByLooping'
import { persistedFreeTextChoicesByQuestionId } from 'utils/persistedFreeTextChoicesByQuestionId'
import { persistedSelectedLanguage } from 'utils/persistedSelectedLanguage'
import { clearResponseChoices } from 'utils/removeRespondentChoiceFromLocalstorage'

const clearRespondentDataFromLocalStorage = (
  respondentId: string,
  surveyId: string
) => {
  clearQuestionsHiddenByDisplayLogic(respondentId, surveyId)
  clearResponseChoices(respondentId, surveyId)
  persistedFreeTextChoicesByQuestionId.clear(respondentId, surveyId)
  clearQuestionsHiddenByLooping(respondentId, surveyId)
}

// Preview-only: also drop progress and the language pick. The non-preview
// beginning-of-survey clear above must NOT do this — wiping progress there
// would force re-agreeing terms after a refresh on the first question, and
// the language pick is only written on the selector page, so wiping it once
// the survey starts would re-prompt on every later refresh.
const clearAllPersistedRespondentState = (
  respondentId: string,
  surveyId: string
) => {
  clearRespondentDataFromLocalStorage(respondentId, surveyId)
  clearPersistedRespondentProgress(respondentId, surveyId)
  persistedSelectedLanguage.clear(respondentId, surveyId)
}

export interface AppState {
  respondentProgress: [
    value: RespondentProgress | undefined,
    set: (newVal: RespondentProgress) => void
  ]
  renderedQuestionnaire: [
    data: RenderedQuestionnaire | undefined,
    loading: boolean,
    error: boolean
  ]
  surveyLocales: [
    data: SurveyLocales | undefined,
    loading: boolean,
    error: boolean
  ]
  selectedLanguage: [value: string | undefined, set: (newVal: string) => void]
  responseChoicePromises: Promise<void>[]
  addResponseChoicePromise: (promise: Promise<void>) => void
}

export const AppStateContext = React.createContext<AppState>({
  respondentProgress: [undefined, noContextMethod],
  renderedQuestionnaire: [undefined, false, false],
  surveyLocales: [undefined, false, false],
  selectedLanguage: [undefined, noContextMethod],
  responseChoicePromises: [],
  addResponseChoicePromise: noContextMethod
})

const AppStateProvider = ({ children }: PropsWithChildren) => {
  const {
    surveyLocales,
    loading: surveyLocalesLoading,
    error: surveyLocalesError
  } = useSurveyLocales()
  const questionnaireParams = useOptionalQuestionnaireParams()
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>()

  // A pick persisted on this device resolves the language without
  // re-prompting, but only once /locales confirms it's still offered — the
  // survey's languages can change between visits, and fetching the
  // questionnaire with a withdrawn language would error. Preview never reads
  // the persisted pick: previewers need the selector every time to check
  // each translation. The preview guard must sit on this read — the
  // clear-on-preview-mount below runs only after the first render, so
  // without it that render would resolve the pick and fire a fetch with it.
  // Snapshot once at mount (not per render): a concurrent tab clearing the
  // key — e.g. a same-rid preview link — must not yank the language out from
  // under a session that already resolved it.
  const [persistedLanguagePick] = useState(() =>
    questionnaireParams && !questionnaireParams.preview
      ? persistedSelectedLanguage.get(
          questionnaireParams.respondentId,
          questionnaireParams.surveyId
        )
      : undefined
  )
  const resolvedSelectedLanguage =
    selectedLanguage ??
    // `!surveyLocales` keeps the pick usable during a /locales outage —
    // dropping it there would fire a no-language fetch and swap a respondent
    // who chose a translation onto the English master mid-survey.
    (persistedLanguagePick &&
    (!surveyLocales ||
      surveyLocales.availableLanguages.includes(persistedLanguagePick))
      ? persistedLanguagePick
      : undefined) ??
    // Single-language surveys resolve right here in render; deriving instead
    // of setting state in an effect avoids a committed frame where the
    // language is still unresolved after /locales arrives.
    (surveyLocales && surveyLocales.availableLanguages.length === 1
      ? surveyLocales.primaryLanguage
      : undefined)

  // Self-heal a withdrawn pick: once /locales confirms the persisted language
  // is no longer offered, drop the key so it can't silently resolve again if
  // the language is later re-added. (The in-memory snapshot is already
  // ignored via the includes() check above.)
  useEffect(() => {
    if (
      persistedLanguagePick &&
      surveyLocales &&
      !surveyLocales.availableLanguages.includes(persistedLanguagePick) &&
      questionnaireParams &&
      !questionnaireParams.preview
    ) {
      persistedSelectedLanguage.clear(
        questionnaireParams.respondentId,
        questionnaireParams.surveyId
      )
    }
  }, [persistedLanguagePick, surveyLocales, questionnaireParams])

  const selectLanguage = useCallback(
    (language: string) => {
      if (questionnaireParams && !questionnaireParams.preview) {
        persistedSelectedLanguage.set(
          questionnaireParams.respondentId,
          questionnaireParams.surveyId,
          language
        )
      }
      setSelectedLanguage(language)
    },
    [questionnaireParams]
  )

  // Defer the questionnaire fetch until we know which language to request.
  // Whenever we know the survey's locales (single- or multi-language) we wait
  // for the language to resolve — via the selector or persisted pick for
  // multi-language surveys, or the auto-select effect below for
  // single-language ones.
  // Fetching before then fires a wasted no-language request that hits the
  // legacy endpoint and returns the English master; that response then races
  // the real `&language=` fetch and can clobber the translated questionnaire
  // (see the stale-response guard in useRenderedQuestionnaire). We only fall
  // back to a no-language fetch when /locales fails (surveyLocales undefined),
  // so a locales outage degrades gracefully rather than hard-blocking the
  // survey.
  const skipQuestionnaireFetch =
    surveyLocalesLoading ||
    (!!surveyLocales &&
      surveyLocales.availableLanguages.length > 0 &&
      !resolvedSelectedLanguage)
  const { renderedQuestionnaire, loading, error } = useRenderedQuestionnaire(
    resolvedSelectedLanguage,
    skipQuestionnaireFetch
  )
  const { respondentProgress, setRespondentProgress } = useRespondentProgress()
  const isPreview = !!questionnaireParams?.preview
  const isFirstQuestion = respondentProgress?.currentEntryPosition === 0

  // Store all response choice promises
  const responseChoicePromisesRef = useRef<Promise<void>[]>([])

  const addResponseChoicePromise = useCallback((promise: Promise<void>) => {
    responseChoicePromisesRef.current.push(promise)
  }, [])

  const getStartRespondentProgress = useCallback(
    (termsAgreed: boolean): RespondentProgress | undefined => {
      if (!questionnaireParams) {
        // TODO: log to sentry
        return undefined
      }

      const { surveyId, respondentId } = questionnaireParams

      return {
        currentEntryPosition: 0,
        previousEntryPosition: -1,
        isCompleted: false,
        isQuotaFull: false,
        isScreened: false,
        isQualityTerminated: false,
        isTermsAgreed: termsAgreed,
        questionnaireLen:
          renderedQuestionnaire?.questionnaire.entries?.length || 0,
        respondentId,
        surveyId
      }
    },
    [questionnaireParams, renderedQuestionnaire]
  )

  // always clear local storage for preview on mount (i.e. page refresh).
  // This includes any persisted respondentProgress under the same rid/sid —
  // preview keeps its own progress in memory only (see useRespondentProgress),
  // so a stale persisted entry would otherwise diverge from the wiped answers.
  useMount(() => {
    const progress = getStartRespondentProgress(true)

    if (!progress) {
      return
    }

    if (isPreview) {
      clearAllPersistedRespondentState(
        questionnaireParams.respondentId,
        questionnaireParams.surveyId
      )
      setRespondentProgress(progress)
    }
  })

  // clear local storage for non-preview if it is beginning of the survey; otherwise, local storage data should remain unchanged in order to allow respondent keep the progress even after page reload
  useUpdateEffect(() => {
    if (!isPreview && isFirstQuestion && questionnaireParams) {
      clearRespondentDataFromLocalStorage(
        questionnaireParams.respondentId,
        questionnaireParams.surveyId
      )
    }
  }, [isFirstQuestion, isPreview, questionnaireParams])

  if (
    renderedQuestionnaire &&
    (!respondentProgress ||
      respondentProgress?.surveyId !== renderedQuestionnaire?.surveyId) &&
    questionnaireParams
  ) {
    const progress = getStartRespondentProgress(false)
    if (progress) {
      setRespondentProgress(progress)
    }
  }

  const contextValue: AppState = useMemo(
    () => ({
      respondentProgress: [respondentProgress, setRespondentProgress],
      renderedQuestionnaire: [renderedQuestionnaire, loading, error],
      surveyLocales: [surveyLocales, surveyLocalesLoading, surveyLocalesError],
      selectedLanguage: [resolvedSelectedLanguage, selectLanguage],
      responseChoicePromises: responseChoicePromisesRef.current,
      addResponseChoicePromise
    }),
    [
      error,
      loading,
      renderedQuestionnaire,
      respondentProgress,
      setRespondentProgress,
      surveyLocales,
      surveyLocalesLoading,
      surveyLocalesError,
      resolvedSelectedLanguage,
      selectLanguage,
      addResponseChoicePromise
    ]
  )

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  )
}

export default AppStateProvider
